import {createDraftSafeSelector} from '@reduxjs/toolkit';

import deviceSticksTypes from '../../consts/device/deviceSticksTypes';
import deviceLockTypes from '../../consts/device/settings/deviceLockTypes';
import settingNames from '../../consts/device/settings/settingNames';
import deviceConnectionStatusService from '../../services/device/deviceConnectionStatusService';
import deviceConnectionTypeService from '../../services/device/deviceConnectionTypeService';
import deviceHelpers from '../../services/device/deviceHelpers';
import deviceKitHelpers from '../../services/device/deviceKitHelpers';
import {t} from '../../services/localization/localizationService';
import {selectInitialDusn} from './session';

const selectIotDevicesState = (state) => state.iotDevices;

const selectIotDevicesList = createDraftSafeSelector(selectIotDevicesState, (state) => state.deviceList);

const selectHolders = createDraftSafeSelector(selectIotDevicesState, (state) =>
    Object.values(state.deviceList).filter((device) => device?.isHolder)
);

const selectIotDevicesDusnList = createDraftSafeSelector(selectIotDevicesState, (state) =>
    state.deviceList ? Object.keys(state.deviceList) : []
);

const selectIsAnyDeviceRegistered = createDraftSafeSelector(selectIotDevicesDusnList, (state) => state.length > 0);

const selectIotDevice = (serialNumber) =>
    createDraftSafeSelector(selectIotDevicesState, (state) => state.deviceList?.[serialNumber]);

const selectIotDeviceData = (serialNumber) =>
    createDraftSafeSelector(selectIotDevicesState, (iotDevicesState) => iotDevicesState.deviceList?.[serialNumber]);

const selectIotDeviceConnected = (serialNumber) =>
    createDraftSafeSelector(selectIotDevicesState, (iotDevicesState) => {
        const connectionStatus = iotDevicesState.deviceList?.[serialNumber]?.connectionStatus;

        return deviceConnectionStatusService.isDeviceConnected(connectionStatus);
    });

const selectDeviceBatteryToSticks = (serialNumber) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState.deviceList?.[serialNumber]?.batteryToSticks
    );

const selectConnectedIotDevicesList = createDraftSafeSelector(
    [selectIotDevicesDusnList, (state) => state],
    (iotDevicesDusnList, state) => iotDevicesDusnList.filter((dusn) => selectIotDeviceConnected(dusn)(state))
);

const selectIotDeviceBatteryCharge = (serialNumber) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState.deviceList?.[serialNumber]?.batteryCharge
    );

const selectIotDeviceAssetId = (serialNumber) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[serialNumber]?.assetId
    );

const selectDeviceCodentify = (serialNumber) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[serialNumber]?.codentify
    );

const selectDeviceModelType = (serialNumber) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[serialNumber]?.modelType
    );

const selectDeviceType = (serialNumber) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[serialNumber]?.type
    );

const selectIsHolderType = (serialNumber) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[serialNumber]?.isHolder
    );

const selectIsDeviceAssetsReceived = (serialNumber) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[serialNumber]?.isDeviceAssetsReceived
    );

const selectIsDeviceConnectInProgress = (serialNumber) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[serialNumber]?.isDeviceConnectInProgress
    );

const selectIsDeviceInitialized = (serialNumber) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[serialNumber]?.isDeviceInitialized
    );

const selectIsDeviceUpdateSettingInProgress = (serialNumber, settingName) =>
    createDraftSafeSelector(selectIotDevicesState, (iotDevicesState) => {
        const device = iotDevicesState?.deviceList?.[serialNumber];

        // i5 rejects any overlapping setting update with ACTIVE_REQUEST_ALREADY_EXISTS error
        if (deviceHelpers.getIsIlumaI5(device?.type)) return device?.deviceUpdateSettingsInProgress?.length > 0;

        return device?.deviceUpdateSettingsInProgress?.includes(settingName);
    });

const selectDeviceUpdateSettingsInProgress = (serialNumber) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[serialNumber]?.deviceUpdateSettingsInProgress
    );

const selectIsDeviceSyncInProgress = (serialNumber) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[serialNumber]?.isDeviceSyncInProgress
    );

const selectIsDeviceSyncIsFailed = (serialNumber) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[serialNumber]?.isDeviceSyncIsFailed
    );

const selectIsDeviceSyncStarted = (serialNumber) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[serialNumber]?.isDeviceSyncStarted
    );

const selectIsDeviceConnectIsFailed = (serialNumber) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[serialNumber]?.isDeviceConnectIsFailed
    );

const selectIsDeviceReconnectInProgress = (serialNumber) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[serialNumber]?.isDeviceReconnectInProgress
    );

const selectIsDeviceConnectionEnd = (serialNumber) =>
    createDraftSafeSelector(selectIotDevicesState, (iotDevicesState) => {
        const device = iotDevicesState?.deviceList?.[serialNumber];

        const isDeviceReconnectInProgress = device?.isDeviceReconnectInProgress ?? false;

        return !isDeviceReconnectInProgress;
    });

const selectIsDeviceAssetsInProgress = (serialNumber) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[serialNumber]?.isDeviceAssetsInProgress
    );

const selectDeviceName = (serialNumber) =>
    createDraftSafeSelector(selectIotDevicesState, (iotDevicesState) =>
        t(iotDevicesState?.deviceList?.[serialNumber]?.nameStrCode)
    );

const selectDeviceBatteryCharge = (serialNumber) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[serialNumber]?.batteryCharge
    );

const selectIsDeviceRegisteredByDusn = (dusn) =>
    createDraftSafeSelector(selectIotDevicesDusnList, (iotDevicesDusnList) => iotDevicesDusnList.includes(dusn));

const selectIsDeviceRegisteredByCodentify = (codentify) =>
    createDraftSafeSelector(selectIotDevicesState, (iotDevicesState) =>
        Object.values(iotDevicesState.deviceList).some((device) => device.codentify === codentify)
    );

const selectIotDeviceByCodentify = (codentify) =>
    createDraftSafeSelector(selectIotDevicesState, (iotDevicesState) =>
        Object.values(iotDevicesState.deviceList).find((device) => device.codentify === codentify)
    );

const selectCurrentDusn = createDraftSafeSelector(selectIotDevicesState, (state) => state.currentDusn);

const selectIsCurrentDusnAutoSelected = createDraftSafeSelector(
    selectIotDevicesState,
    (state) => state.isCurrentDusnAutoSelected
);

const selectSortedChargers = createDraftSafeSelector(selectIotDevicesList, (deviceList) =>
    deviceHelpers.getSortedChargers(deviceList)
);

const selectSortedDeviceKits = createDraftSafeSelector(
    selectIotDevicesList,
    selectSortedChargers,
    (deviceList, chargers) =>
        chargers.map(({serialNumber}) => deviceKitHelpers.resolveDeviceKit(deviceList, serialNumber))
);

// currentDusn only ever holds a registered device, so setup falls back to initialDusn.
const selectActiveDusn = createDraftSafeSelector(
    selectInitialDusn,
    selectCurrentDusn,
    (initialDusn, currentDusn) => initialDusn || currentDusn
);

const selectCurrentAssetId = createDraftSafeSelector(
    selectCurrentDusn,
    selectIotDevicesState,
    (currentDusn, iotDevicesState) => iotDevicesState?.deviceList?.[currentDusn]?.assetId
);

const selectDeviceKit = (serialNumber) =>
    createDraftSafeSelector(selectIotDevicesState, (iotDevicesState) =>
        deviceKitHelpers.resolveDeviceKit(iotDevicesState.deviceList, serialNumber)
    );

const selectCurrentDeviceKit = createDraftSafeSelector(
    (state) => state,
    selectCurrentDusn,
    (state, currentDusn) => {
        return selectDeviceKit(currentDusn)(state);
    }
);

const selectCurrentHolderDusn = createDraftSafeSelector(selectCurrentDeviceKit, ({holder}) => holder?.serialNumber);

const selectCurrentDevice = createDraftSafeSelector(
    selectCurrentDusn,
    selectIotDevicesList,
    (currentDusn, deviceList) => deviceList?.[currentDusn] || {}
);

const selectCurrentDeviceName = createDraftSafeSelector([selectCurrentDusn, (state) => state], (currentDusn, state) =>
    selectDeviceName(currentDusn)(state)
);

const selectCurrentDeviceType = createDraftSafeSelector(
    selectCurrentDusn,
    selectIotDevicesList,
    (currentDusn, deviceList) => deviceList?.[currentDusn]?.type
);

const selectIsCurrentIlumaI2Type = createDraftSafeSelector(selectCurrentDeviceType, (deviceType) =>
    deviceHelpers.getIsIlumaI2(deviceType)
);

const selectIsCurrentIlumaI5Type = createDraftSafeSelector(selectCurrentDeviceType, (deviceType) =>
    deviceHelpers.getIsIlumaI5(deviceType)
);

const selectCurrentConnectionType = createDraftSafeSelector(
    selectCurrentDevice,
    (iotDeviceData) => iotDeviceData?.connectionType
);

const selectIsLTEConnectionType = createDraftSafeSelector(selectCurrentConnectionType, (connectionType) =>
    deviceConnectionTypeService.isCellular(connectionType)
);

const selectIsWiFiConnectionType = createDraftSafeSelector(selectCurrentConnectionType, (connectionType) =>
    deviceConnectionTypeService.isWiFi(connectionType)
);

const selectDeviceFirmware = (dusn) =>
    createDraftSafeSelector(selectIotDevicesState, (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.firmware);

const selectDeviceAvailableFirmwareVersion = (dusn) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.firmware?.availableFirmwareVersion
    );

const selectDeviceFirmwareReleaseNotes = (dusn) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.firmware?.releaseNotes
    );

const selectDeviceFirmwareReleaseNotesType = (dusn) =>
    createDraftSafeSelector(
        (state) => selectDeviceFirmwareReleaseNotes(dusn)(state),
        (releaseNotes) => releaseNotes?.[releaseNotes.length - 1]?.type
    );

const selectDeviceFwuProgress = (dusn) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.firmware?.progress || 0
    );

const selectFwuStatus = (dusn) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.firmware?.status
    );

const selectDeviceFirmwareVersion = (dusn) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.firmwareVersion
    );

const selectDeviceSetting = (dusn, settingName) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.settings?.[settingName]
    );

const selectIsAutomaticFWInstallationActive = (dusn) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.firmware?.isAutomaticFWInstallationActive
    );

const selectDeviceCustomUI = (dusn) => selectDeviceSetting(dusn, settingNames.CUSTOM_UI);

const selectDeviceBrightness = (dusn) => selectDeviceSetting(dusn, settingNames.BRIGHTNESS);

const selectDeviceFlexBatteryMode = (dusn) => selectDeviceSetting(dusn, settingNames.FLEX_BATTERY);

const selectDeviceFlexPuffStatus = (dusn) => selectDeviceSetting(dusn, settingNames.FLEX_PUFF);

const selectDevicePauseMode = (dusn) => selectDeviceSetting(dusn, settingNames.PAUSE_MODE);

const selectDeviceLockType = (dusn) => selectDeviceSetting(dusn, settingNames.DEVICE_LOCK);

const selectAutoStartState = (dusn) => selectDeviceSetting(dusn, settingNames.AUTO_START);

const selectDeviceLiftUpGesture = (dusn) => selectDeviceSetting(dusn, settingNames.LIFT_UP_GESTURE);

const selectDeviceDoubleTapGesture = (dusn) => selectDeviceSetting(dusn, settingNames.DOUBLE_TAP_GESTURE);

const selectDeviceLedIntensity = (dusn) => selectDeviceSetting(dusn, settingNames.LED_INTENSITY);

const selectBatteryCharged = (dusn) => selectDeviceSetting(dusn, settingNames.BATTERY_CHARGED);

const selectHeatingVibrations = (dusn) => selectDeviceSetting(dusn, settingNames.HEATING_VIBRATION);

const selectVibrationIntensity = (dusn) => selectDeviceSetting(dusn, settingNames.VIBRATION_INTENSITY);

const selectTunabilityState = (dusn) => selectDeviceSetting(dusn, settingNames.TUNABILITY);

const selectRememberHeatingProfileState = (dusn) => selectDeviceSetting(dusn, settingNames.REMEMBER_HEATING_PROFILE);

const selectRangeModeState = (dusn) => selectDeviceSetting(dusn, settingNames.RANGE_MODE);

const selectIsDeviceSettingLocked = (dusn, settingName) =>
    createDraftSafeSelector(
        [selectDeviceLockType(dusn), selectDeviceUpdateSettingsInProgress(dusn)],
        (deviceLock, deviceUpdateSettingsInProgress) => {
            const IGNORED_LOCK_SETTINGS = [settingNames.CUSTOM_UI, settingNames.BRIGHTNESS, settingNames.DEVICE_LOCK];

            if (IGNORED_LOCK_SETTINGS.includes(settingName)) return false;

            // not selectIsDeviceUpdateSettingInProgress: on i5 it reports any update as in progress
            const isDeviceLockingInProgress = deviceUpdateSettingsInProgress?.includes(settingNames.DEVICE_LOCK);

            return deviceLock === deviceLockTypes.ENABLED || !!isDeviceLockingInProgress;
        }
    );

const selectDeviceSynchronizeUpdateDate = (dusn) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.synchronizeUpdateDate
    );

const selectDeviceConnectionType = (dusn) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.connectionType
    );

const selectLtePairingError = () =>
    createDraftSafeSelector(selectIotDevicesState, (iotDevicesState) => iotDevicesState?.ltePairingError);

const selectDeviceAuthToken = (dusn) =>
    createDraftSafeSelector(selectIotDevicesState, (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.authToken);

const selectDusnByAssetId = (assetId) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) =>
            assetId &&
            Object.values(iotDevicesState?.deviceList || {}).find((device) => device?.assetId === assetId)?.serialNumber
    );

const selectDusnByCodentify = (codentify) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) =>
            codentify &&
            Object.values(iotDevicesState?.deviceList || {}).find((device) => device?.codentify === codentify)
                ?.serialNumber
    );

const selectIsCurrentDeviceConnected = createDraftSafeSelector(
    [selectCurrentDusn, (state) => state],
    (currentDusn, state) => selectIotDeviceConnected(currentDusn)(state)
);

const selectIsCurrentHolderConnected = createDraftSafeSelector(
    [selectCurrentHolderDusn, (state) => state],
    (currentHolderDusn, state) => selectIotDeviceConnected(currentHolderDusn)(state)
);

const selectCurrentTargetDusn = (isHolder) =>
    createDraftSafeSelector(selectCurrentDusn, selectCurrentHolderDusn, (currentDusn, currentHolderDusn) =>
        isHolder ? currentHolderDusn : currentDusn
    );

const selectDeviceADData = (dusn) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.advancedDiagnostic || {}
    );

const selectDeviceUsageDataByPeriodType = (dusn, periodType) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.usage?.[periodType] || []
    );

const selectDeviceUsageStickType = (dusn) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.usage?.stickType || deviceSticksTypes.TEREA
    );

const selectIsExperienceDataLoading = (dusn) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.isExperienceDataLoading
    );

const selectExperienceCalibration = (dusn) =>
    createDraftSafeSelector(selectIotDevicesList, (deviceList) => deviceList?.[dusn]?.experience?.calibration || {});

const selectIsCalibrationActive = (dusn) =>
    createDraftSafeSelector(selectExperienceCalibration(dusn), (calibration) => {
        const {n_sticks_identified, min_n_experiences_required} = calibration || {};

        return n_sticks_identified < min_n_experiences_required;
    });

const selectExperienceInsightsAndTips = (dusn) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.experience?.insightsAndTips || {}
    );

const selectExperienceScore = (dusn) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.experience?.experienceScore || {}
    );

const selectExperienceInferenceRawItems = (dusn) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.experience?.rawItems
    );

const selectExperienceTipsToConsider = (dusn) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.experience?.tipsToConsider || {}
    );

const selectExperienceInferenceTimestamp = (dusn) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.experience?.inferenceTimestamp
    );

const selectDeviceLastChargerDusn = (dusn) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.lastChargerDusn
    );

const selectLastConnectionStatusUpdateDate = (dusn) =>
    createDraftSafeSelector(
        selectIotDevicesState,
        (iotDevicesState) => iotDevicesState?.deviceList?.[dusn]?.lastConnectionStatusUpdateDate
    );

export {
    selectActiveDusn,
    selectAutoStartState,
    selectBatteryCharged,
    selectConnectedIotDevicesList,
    selectCurrentAssetId,
    selectCurrentConnectionType,
    selectCurrentDevice,
    selectCurrentDeviceKit,
    selectCurrentDeviceName,
    selectCurrentDeviceType,
    selectCurrentDusn,
    selectCurrentHolderDusn,
    selectCurrentTargetDusn,
    selectDeviceADData,
    selectDeviceAuthToken,
    selectDeviceAvailableFirmwareVersion,
    selectDeviceBatteryCharge,
    selectDeviceBatteryToSticks,
    selectDeviceBrightness,
    selectDeviceCodentify,
    selectDeviceConnectionType,
    selectDeviceCustomUI,
    selectDeviceDoubleTapGesture,
    selectDeviceFirmware,
    selectDeviceFirmwareReleaseNotes,
    selectDeviceFirmwareReleaseNotesType,
    selectDeviceFirmwareVersion,
    selectDeviceFlexBatteryMode,
    selectDeviceFlexPuffStatus,
    selectDeviceFwuProgress,
    selectDeviceKit,
    selectDeviceLastChargerDusn,
    selectDeviceLedIntensity,
    selectDeviceLiftUpGesture,
    selectDeviceLockType,
    selectDeviceModelType,
    selectDeviceName,
    selectDevicePauseMode,
    selectDeviceSetting,
    selectDeviceSynchronizeUpdateDate,
    selectDeviceType,
    selectDeviceUpdateSettingsInProgress,
    selectDeviceUsageDataByPeriodType,
    selectDeviceUsageStickType,
    selectDusnByAssetId,
    selectDusnByCodentify,
    selectExperienceCalibration,
    selectExperienceInferenceRawItems,
    selectExperienceInferenceTimestamp,
    selectExperienceInsightsAndTips,
    selectExperienceScore,
    selectExperienceTipsToConsider,
    selectFwuStatus,
    selectHeatingVibrations,
    selectHolders,
    selectIotDevice,
    selectIotDeviceAssetId,
    selectIotDeviceBatteryCharge,
    selectIotDeviceByCodentify,
    selectIotDeviceConnected,
    selectIotDeviceData,
    selectIotDevicesDusnList,
    selectIotDevicesList,
    selectIsAnyDeviceRegistered,
    selectIsAutomaticFWInstallationActive,
    selectIsCalibrationActive,
    selectIsCurrentDeviceConnected,
    selectIsCurrentDusnAutoSelected,
    selectIsCurrentHolderConnected,
    selectIsCurrentIlumaI2Type,
    selectIsCurrentIlumaI5Type,
    selectIsDeviceAssetsInProgress,
    selectIsDeviceAssetsReceived,
    selectIsDeviceConnectInProgress,
    selectIsDeviceConnectionEnd,
    selectIsDeviceConnectIsFailed,
    selectIsDeviceInitialized,
    selectIsDeviceReconnectInProgress,
    selectIsDeviceRegisteredByCodentify,
    selectIsDeviceRegisteredByDusn,
    selectIsDeviceSettingLocked,
    selectIsDeviceSyncInProgress,
    selectIsDeviceSyncIsFailed,
    selectIsDeviceSyncStarted,
    selectIsDeviceUpdateSettingInProgress,
    selectIsExperienceDataLoading,
    selectIsHolderType,
    selectIsLTEConnectionType,
    selectIsWiFiConnectionType,
    selectLastConnectionStatusUpdateDate,
    selectLtePairingError,
    selectRangeModeState,
    selectRememberHeatingProfileState,
    selectSortedChargers,
    selectSortedDeviceKits,
    selectTunabilityState,
    selectVibrationIntensity,
};
